<template>
  <button
    class="fw-filterpel-pagination-button prev"
    :disabled="isPrevDisabled"
    :aria-label="languageStrings.previous[languageStore.language]"
    @click="!isPrevDisabled ? goToPage('prev') : null"
  />
  <div class="fw-filterpel-pagination-pages">
    <button
      v-for="i in totalPages"
      :key="`page-${i}`"
      :class="[`fw-filterpel-pagination-pages-entry`, { active: i === currentPage }]"
      @click="goToPage(i)"
    >
      {{ i }}
    </button>
  </div>
  <button
    class="fw-filterpel-pagination-button next"
    :disabled="isNextDisabled"
    :aria-label="languageStrings.next[languageStore.language]"
    @click="!isNextDisabled ? goToPage('next') : null"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, useHost, getCurrentInstance } from "vue";

import useStateStore from "@stores/state";
import useEntriesStore from "@stores/entries";
import useLanguageStore from "@/stores/language";

// define props
const props = defineProps<{
  elemId: string,
  current: string,
  total: string,
}>();

// define stores
const stateStore = useStateStore();
const entriesStore = useEntriesStore();
const languageStore = useLanguageStore();

// define language strings
const languageStrings: ILanguageStrings = {
  previous: {
    de: "Gehe zur vorherigen Seite",
    en: "Go to previous page"
  },
  next: {
    de: "Gehe zur nächsten Seite",
    en: "Go to next page"
  }
};

// computed
const currentPage = computed(() => entriesStore.paginationCurrent);
const totalPages = computed(() => entriesStore.paginationTotal);
const isPrevDisabled = computed(() => currentPage.value === 1);
const isNextDisabled = computed(() => currentPage.value >= totalPages.value);

// methods
const goToPage = (page: string | number) => {
  if (typeof page === "number") {
    entriesStore.paginationMove(props.elemId, page);
  }
  if (page === "prev") {
    entriesStore.paginationMove(props.elemId, currentPage.value - 1);
  }
  if (page === "next") {
    entriesStore.paginationMove(props.elemId, currentPage.value + 1);
  }
};

// livecycle - mounted
onMounted(() => {
  // init instance on state
  const hostNode = useHost() ?? getCurrentInstance()?.proxy?.$el;
  stateStore.addInstance(props.elemId, "pagination", hostNode);

  // set current and total from props
  entriesStore.paginationCurrent = Number(props.current);
  entriesStore.paginationTotal = Number(props.total);
});

</script>
